import styled from '@emotion/styled';

import { colors } from '../../colors';
import {
  getSelectDropdownStyles,
  getSelectFieldStyles,
  getSelectOptionStyles,
  getSelectPanelStyles,
  getSelectSearchContainerStyles,
  getSelectSearchInputStyles,
  getSelectSizeStyles,
  getSelectTagStyles,
} from './select-styles';
import type { SelectSize, SelectStyleProps } from './types';

export const Field = styled.div<{
  $size: SelectSize;
  $styleProps?: SelectStyleProps | undefined;
  $multi?: boolean;
  $hasSelections?: boolean;
  $open?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: ${({ $multi, $hasSelections }) =>
    $multi && $hasSelections ? 'flex-start' : 'center'};
  flex-wrap: ${({ $multi }) => ($multi ? 'wrap' : 'nowrap')};
  gap: 4px;
  width: ${({ $styleProps }) => $styleProps?.width ?? '100%'} !important;
  max-width: ${({ $styleProps }) => $styleProps?.width ?? '100%'} !important;
  min-width: ${({ $styleProps }) => $styleProps?.width ?? 'auto'} !important;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  overflow: hidden;
  flex-shrink: 0;

  ${({ $size, $styleProps }) => {

    if ($styleProps?.padding) return '';

    const basePadding = getSelectSizeStyles($size);

    switch ($size) {
      case 'small':
        return basePadding.replace('padding: 8px 44px 8px 12px', 'padding: 8px 72px 8px 12px');
      case 'large':
        return basePadding.replace('padding: 12px 48px 12px 16px', 'padding: 12px 76px 12px 16px');
      case 'medium':
      default:
        return basePadding.replace('padding: 10px 44px 10px 14px', 'padding: 10px 72px 10px 14px');
    }
  }}

  ${({ $styleProps, $open }) => getSelectFieldStyles($styleProps, $open)}
`;

export const InlineInput = styled.input<{
  $styleProps?: SelectStyleProps;
}>`
  flex: 1 1 auto;
  min-width: 96px;
  max-width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: ${({ $styleProps }) => $styleProps?.fontSize ?? '16px'};
  color: ${({ $styleProps }) => $styleProps?.color ?? colors.gray[800]};
  padding: 4px 0;
  font-weight: 400;

  &::placeholder {
    color: ${({ $styleProps }) => $styleProps?.color ?? colors.gray[400]};
    font-weight: 400;
  }

  &:focus {
    outline: none;
  }
`;

export const Tag = styled.span<{
  $styleProps?: SelectStyleProps;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  flex-shrink: 0;
  max-width: 100%;

  ${({ $styleProps }) => getSelectTagStyles($styleProps)}
`;

export const TagText = styled.span`
  flex: 1 1 0;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const RemoveBtn = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.primary[300]};
  cursor: pointer;
  padding: 0;
  line-height: 1;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.15s ease-in-out;
  opacity: 0.7;

  &:hover {
    background: ${colors.primary[300]}20;
    color: ${colors.primary[600]};
    opacity: 1;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const Panel = styled.div<{
  $styleProps?: SelectStyleProps | undefined;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${({ $styleProps }) => getSelectPanelStyles($styleProps)}
`;

export const SearchContainer = styled.div<{
  $size?: SelectSize;
}>`
  position: relative;
  ${({ $size }) => getSelectSearchContainerStyles($size)}
`;

export const SearchInput = styled.input<{
  $styleProps?: SelectStyleProps;
  $size?: SelectSize;
}>`
  ${({ $styleProps, $size }) => getSelectSearchInputStyles($styleProps, $size)}
`;

export const Dropdown = styled.div<{
  $styleProps?: SelectStyleProps | undefined;
  $size?: SelectSize;
}>`
  width: 100%;
  flex: 1 1 auto;

  ${({ $styleProps, $size }) => getSelectDropdownStyles($styleProps, $size)}
`;

export const OptionRow = styled.div<{
  $active?: boolean;
  $selected?: boolean;
  $disabled?: boolean;
  $styleProps?: SelectStyleProps | undefined;
  $size?: SelectSize;
}>`
  ${({ $active = false, $selected = false, $disabled = false, $styleProps, $size }) =>
    getSelectOptionStyles($active, $selected, $disabled, $styleProps, $size)}
`;

export const Check = styled.span`
  margin-left: auto;
  font-size: 14px;
  color: ${colors.primary[300]};
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ClearBtn = styled.button<{
  $multi?: boolean;
  $hasSelections?: boolean;
}>`
  border: 0;
  background: transparent;
  color: ${colors.gray[500]};
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-in-out;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    background: ${colors.gray[200]};
    color: ${colors.gray[700]};
    transform: ${({ $multi, $hasSelections }) =>
      $multi && $hasSelections ? 'scale(1.05)' : 'translateY(-50%) scale(1.05)'};
  }

  &:active {
    transform: ${({ $multi, $hasSelections }) =>
      $multi && $hasSelections ? 'scale(0.95)' : 'translateY(-50%) scale(0.95)'};
  }
`;

export const Placeholder = styled.span`
  color: ${colors.gray[400]};
  font-weight: 400;
  flex: 1 1 0;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const SelectedText = styled.span<{
  $styleProps?: SelectStyleProps;
}>`
  color: ${({ $styleProps }) => $styleProps?.color ?? colors.gray[800]};
  font-weight: 400;
  flex: 1 1 0;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const DropdownIcon = styled.div<{
  $open: boolean;
  $disabled: boolean;
}>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: ${({ $open }) =>
    $open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${colors.gray[500]};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  flex-shrink: 0;
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
  }

  &:hover {
    color: ${({ $disabled }) => ($disabled ? colors.gray[500] : colors.gray[700])};
  }
`;